<template>
    <v-layout row wrap>
        <v-flex xs12 v-if="loading">
            <v-progress-linear :indeterminate="true"></v-progress-linear>
        </v-flex>
        <v-flex xs12 v-if="!loading">
            <v-card flat>
                <v-card-title class="primary white--text headline">
                    KPI
                    <v-spacer></v-spacer>
                    <date-range-selector v-model="dateRange"  @change="handleDateChange()"></date-range-selector>
                </v-card-title>
                <v-layout row wrap>
                    <v-flex xs12>
                        <v-layout row wrap>
                            <v-flex xs6 md3>
                                <value-box :value="bpi" unit="kWh/m²" title="Annualized Building Performance Index"
                                           icon="fa-smile" class="green-icon"
                                           explanation>
                                    <p>
                                        Annualized Building Performance Index or Energy Use Intensity (EUI) is calculated by dividing the annualized total energy consumption for the current/selected range ("{{dateRange.start}} to {{dateRange.end}}") with gross floor area of the building.
                                    </p>
                                    <p>
                                        It must be noted that the total energy consumption in this case is the sum of energy consumption of all equipment that has been mapped to the analytics platform. It does not include systems like fire alarms, lifts and escalators, plumbing and drainage etc.
                                    </p>
                                    <p>
                                        Annualized total energy consumption = ((total energy consumption for the current/selected range) / (number of days in the current/selected range)) * 365
                                    </p>
                                    <p>
                                        High Annualized Building Performance Index indicates inefficiency in equipment operation. According to EMSD the total site EUI for commercial buildings with centralized cooling is 132 kWh/m2/annum. HVAC systems consume about 68% of the total site energy, we can assume HVAC EUI benchmark to be about 90 kWh/m2/annum.
                                    </p>
                                    <p>
                                        References:
                                    </p>
                                    <ul>
                                        <li><a class="white--text" target="_blank" href="https://ecib.emsd.gov.hk/en/indicator_cmc.htm">EMSD EUI</a></li>
                                        <li><a class="white--text" target="_blank" href="https://www.researchgate.net/publication/315416147_A_Study_on_Energy_Performance_of_30_Commercial_Office_Buildings_in_Hong_Kong">HVAC breakdown for office buildings</a></li>
                                    </ul>
                                </value-box>
                            </v-flex>
                            <v-flex xs6 md3>
                                <value-box :value="oci" unit="%" title="Occupant Comfort Index"
                                           :icon="oci>50 ? 'fa-smile': 'fa-meh'"
                                           v-bind:class="{ 'green-icon': oci>50}"
                                           explanation
                                >
                                    <p>Occupant Comfort Index is defined as the % of AHUs with ΔSp lower than 2°C in the current/selected date range ("{{dateRange.start}} to {{dateRange.end}}"). ΔSp is the mean absolute difference between CDT_SP (cool duct setpoint) and CD_TEM (cool duct actual temperature).</p>
                                    <p>The higher the occupant comfort index the higher % of AHUs are following the setpoint and meeting demand requirements from the space. </p>
                                    <p>According to ASHRAE Standard 55 Thermal Environmental Conditions for Occupancy, the ideal range for temperature is 22 - 27 °C. As most AHUs are designed to supply air within this range, Higher Occupant Comfort Index will provide a ensure occupants or tenants in the space are thermally comfortable. </p>
                                    <p>
                                        References:
                                    </p>
                                    <ul>
                                        <li><a class="white--text" target="_blank" href="http://arco-hvac.ir/wp-content/uploads/2015/11/ASHRAE-55-2010.pdf ">ASHRAE STANDARD 55: Thermal Environmental Conditions for Human Occupancy</a></li>
                                    </ul>
                                </value-box>
                            </v-flex>
                            <v-flex xs6 md3>
                                <value-box :value="ehi" use-int unit=""
                                           title="Maintenance Fault Index" :icon="ehi<10 ? 'fa-smile': 'fa-meh'"
                                           v-bind:class="{ 'green-icon': ehi<10}"
                                           explanation
                                >
                                    <p>
                                        Defined as the number of critical or high severity maintenance type faults on critical equipment during the current/selected date range ("{{dateRange.start}} to {{dateRange.end}}"). Chiller Plant Equipment and Air Handling Units (AHU) are considered as critical equipment as they have the highest power consumption and are expensive to repair and maintain.
                                    </p>
                                    <p>
                                        Higher maintenance fault index indicates inefficiency in maintenance of the Chiller Plant and AHU equipment.
                                    </p>
                                    <p>Recommendations for maintenance and rectification of maintenance related defects are provided on the recommended action section under each notification or on notification breakdown. Recommendations for rectifications of maintenance faults are as per ASHRAE maintenance standard 180-2018. </p>
                                    <p>
                                        Standard 180 establishes minimum HVAC inspection and maintenance requirements that preserve a system's ability to achieve acceptable thermal comfort, energy efficiency and indoor air quality in new and existing commercial buildings.
                                    </p>
                                    <p>
                                        Being proactive in rectifying these faults will lead to continuous improvement to workflows, increased uptime and reduced spending on unnecessary repairs.
                                    </p>
                                    <p>
                                        References:
                                    </p>
                                    <ul>
                                        <li><a class="white--text" target="_blank" href="https://www.ashrae.org/technical-resources/bookstore/standards-180-and-211">ASHRAE STANDARD 180: Standard Practice for Inspection and Maintenance of Commercial Building HVAC Systems</a></li>
                                    </ul>
                                </value-box>
                            </v-flex>
                            <v-flex xs6 md3>
                                <value-box :value="powerDiff" unit="%"
                                           title="Energy Saving"
                                           :icon="powerDiff>0 ? 'fa-smile': 'fa-meh'"
                                           v-bind:class="{ 'green-icon': powerDiff>0 }"
                                           explanation
                                >
                                    <p>
                                        Computed by comparing the % difference between energy consumption of the current/selected date range ("{{dateRange.start}} to {{dateRange.end}}") with that of the baseline range ("{{pastRange.start}} to {{pastRange.end}}").
                                    </p>
                                    <p>
                                        Higher Year on Year (YoY) energy savings indicates increased HVAC equipment operational efficiency. The analytics software continuously provides opportunities that improve energy efficiency and HVAC asset life while reducing downtime.
                                    </p>
                                </value-box>
                            </v-flex>

                        </v-layout>
                    </v-flex>
                    <v-flex xs12>
                        <v-layout row wrap>
                            <v-flex xs12 md4>
                                <vue-plotly v-if="ahuPowerData" disable-responsive :data="ahuPowerData" :layout="ahuPowerLayout" :options="{}"/>
                            </v-flex>
                            <v-flex xs12 md4>
                                <vue-plotly v-if="ahuOnData" disable-responsive :data="ahuOnData" :layout="ahuOnLayout" :options="{}"/>
                            </v-flex>

                            <v-flex xs12 md4>
                                <vue-plotly v-if="ahuDiffData" disable-responsive :data="ahuDiffData"
                                            @click="handleDiffClick"
                                            :layout="ahuDiffLayout" :options="{}"/>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
            </v-card>
        </v-flex>
    </v-layout>
</template>

<script>

    import moment from 'moment'
    import {callAPI,getNotification} from '@/helper'
    import VuePlotly from "../components/VuePlotly";
    import ValueBox from "../components/ValueBox";
    import DateRangeSelector from "../components/DateRangeSelector";


    export default {
        name: 'AAKPI',
        components: {VuePlotly,ValueBox,DateRangeSelector},
        data () {
            return {
                menu:false,
                oci:null,
                ehi:null,
                baseDate:  moment().subtract(15, "days"),
                endDate:  moment().subtract(1, "days"),
                dateRange:  {start:moment().subtract(15, "days").format("YYYY-MM-DD"),end:moment().subtract(1, "days").format("YYYY-MM-DD")},
                pastRange:  [],
                loading:true,
                ahuOnData:null,
                chillers:[],
                pumps:[],
                ahus:[],
                powerDiff:null,
                bpi:null,
                ahuOnLayout: {
                    title: "AHU Run Time (hr)",
                    showlegend: false,
                    xaxis:{
                        showgrid:false,
                        ticks:'',
                        showticklabels:false
                    },
                    margin:{
                        t:160,
                        l:100
                    }
                },
                ahuDiffData:null,
                ahuDiffLayout: {
                    title: "ZoneTemp ΔSp (absΔ°C)",
                    showlegend: false,
                    xaxis:{
                        range:[0,3],
                        showgrid:false,
                        ticks:'',
                        showticklabels:false
                    },
                    margin:{
                        t:160,
                        l:100
                    }
                },

                ahuPowerData:null,
                ahuPowerLayout: {
                    title: "Pump Run Time (hr)",
                    showlegend: false,
                    xaxis:{
                        showgrid:false,
                        ticks:'',
                        showticklabels:false
                    },
                    margin:{
                        t:160,
                        l:100
                    }
                },
            }
        },
        methods:{
            handleDateChange(){
                this.baseDate = moment(this.dateRange.start)
                this.endDate = moment(this.dateRange.end)
                this.getData()
            },
            addArrays(ar1, ar2){
                let ar3 = [];
                for(let i in ar1)
                    if(!ar2[i]){
                        ar3.push(ar1[i]);
                    } else {
                        ar3.push(ar1[i] + ar2[i]);
                    }
                return ar3;
            },
            handleDiffClick(data){
                this.$router.push({
                    path: '/sensors',
                    query: {
                        variableX: 'Space_Temp_SP',
                        variableY: 'Space_Temp',
                        equipment: data.points[0].y,
                        rangeStart: this.dateRange.start,
                        rangeEnd: this.dateRange.end,
                    }
                })
            },
            getData(){
                let me=this;
                me.loading=true;
                me.bpi=null
                me.oci=null
                me.ehi=null
                me.powerDiff=null
                me.chillers=[]
                me.pumps=[]
                me.ahus=[]
                let currentRange=[me.baseDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),me.endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss')]
                let pastRange=[moment(currentRange[0]).subtract(1, "month").startOf('day').format('YYYY-MM-DD HH:mm:ss'),moment(currentRange[1]).subtract(1, "month").endOf('day').format('YYYY-MM-DD HH:mm:ss')]
                me.pastRange={start:moment(currentRange[0]).subtract(1, "month").startOf('day').format('YYYY-MM-DD'),end:moment(currentRange[1]).subtract(1, "month").endOf('day').format('YYYY-MM-DD')}
                for (let i in this.$store.state.pointsListStore.pointsList.equipment_details) {
                    let equip = this.$store.state.pointsListStore.pointsList.equipment_details[i]
                    if(equip.equipment_type.toLowerCase().startsWith('chiller')){
                        me.chillers.push(equip.equipment_name)
                    } else if(equip.equipment_type.toLowerCase().startsWith('ahu')){
                        me.ahus.push(equip.equipment_name)
                    } else if (equip.equipment_type==="SCP"){
                        me.pumps.push(equip.equipment_name)
                    }
                }
                if(me.ahus.length>0) {
                    callAPI('data2/',
                        {
                            'filters': {
                                'facility_name': me.$store.state.pointsListStore.pointsList.facility_name,
                                'equipment_type_regex': 'ecfan|ahu',
                                'sensor_names': ['SA_Fan_Status', 'Space_Temp','Space_Temp_SP'],
                                'date_range': currentRange,
                            }
                        }
                    ).then(function (result) {
                        let increm = 1 / me.$store.state.pointsListStore.pointsList.sampling_freq
                        let ahuNames = []
                        let ahuOn = []
                        let ahuDiff = []
                        for (let ahuName of me.ahus) {
                            if (result.data[ahuName + ' - SA_Fan_Status']&&result.data[ahuName + ' - Space_Temp']) {
                                ahuNames.push(ahuName)
                                let diffArray = []
                                let ontime = 0
                                for (let flowindex in result.data[ahuName + ' - SA_Fan_Status']) {
                                    let flow = result.data[ahuName + ' - SA_Fan_Status'][flowindex]
                                    if (flow > 0) {
                                        ontime = ontime + increm
                                        if (result.data[ahuName + ' - Space_Temp'] && result.data[ahuName + ' - Space_Temp_SP']) {
                                            diffArray.push(Math.abs(result.data[ahuName + ' - Space_Temp'][flowindex] - result.data[ahuName + ' - Space_Temp_SP'][flowindex]))
                                        }

                                    }

                                }
                                ahuOn.push(ontime)
                                if (diffArray.length == 0) {
                                    ahuDiff.push(0)
                                } else {
                                    ahuDiff.push(diffArray.reduce((p, c) => p + c, 0) / diffArray.length)
                                }
                            }
                        }
                        me.oci = (ahuDiff.filter(x => x < 2).length / ahuDiff.length) * 100
                        me.ahuOnData = [{
                            y: ahuNames,
                            x: ahuOn,
                            type: 'bar',
                            marker: {
                                color: 'rgba(31, 119, 180,0.5)',
                            },
                            orientation: 'h'
                        }]
                        me.ahuOnLayout.height = Math.max(700, 25 * ahuNames.length)
                        me.ahuDiffData = [{
                            y: ahuNames,
                            x: ahuDiff,
                            type: 'bar',
                            marker: {
                                color: 'rgba(148, 103, 189,0.5)',
                            },
                            orientation: 'h'
                        }]
                        me.ahuDiffLayout.height = Math.max(700, 25 * ahuNames.length)
                        me.loading=false
                    })
                }
                if(me.pumps.length>0) {
                    callAPI('data2/',
                        {
                            'filters': {
                                'facility_name': me.$store.state.pointsListStore.pointsList.facility_name,
                                'equipment_type': 'SCP',
                                'sensor_names': ['on'],
                                'date_range': currentRange,
                            }
                        }
                    ).then(function (pumpRes) {
                        let increm = 1 / me.$store.state.pointsListStore.pointsList.sampling_freq
                        let pumpNames = []
                        let pumpOn = []
                        for (let pump of me.pumps) {
                            if (pumpRes.data[pump + ' - on']) {
                                pumpNames.push(pump)
                                let ontime = 0
                                for (let flowindex in pumpRes.data[pump + ' - on']) {
                                    let flow = pumpRes.data[pump + ' - on'][flowindex]
                                    if (flow > 0) {
                                        ontime = ontime + increm

                                    }
                                }
                                pumpOn.push(ontime)
                            }
                        }
                        me.ahuPowerData = [{
                            y: pumpNames,
                            x: pumpOn,
                            type: 'bar',
                            marker: {
                                color: 'rgba(140, 86, 75,0.5)',
                            },
                            orientation: 'h'
                        }]
                        me.ahuPowerLayout.height = Math.max(700, 25 * pumpNames.length)

                    })
                }
                getNotification({
                    facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                    date_range:currentRange,
                }).then(function(notifResult) {
                    let rangeFaults = notifResult.result
                    me.ehi=rangeFaults.filter(x=>x.topic==="maintenance"&&(x.severity==="high"||x.severity==="critical")).length
                })

            }
        },
        beforeMount() {
            let maxDate=this.$store.state.pointsListStore.pointsList.data_max_date;
            if(maxDate){
                this.baseDate=moment(maxDate).subtract(15, "days")
                this.endDate=moment(maxDate).subtract(1, "days")
                this.dateRange={start:this.baseDate.format('YYYY-MM-DD'),end:this.endDate.format('YYYY-MM-DD')}
            }
            this.getData();
        }



    }
</script>
